import React from 'react';
import { Fit, ImageSlot, ItemText, fs } from '../parts/fields';
import { EmptyNote, Head, Logo } from '../parts/chrome';
import { bool, img, items, useSlide } from '../parts/context';
import { DeckRep } from '../types';
import { fill } from '../text';

const initials = (name: string): string => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('');
const prettyUrl = (u: string): string => u.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');

// #region Avatar
const Avatar: React.FunctionComponent<{ url: string | null; name: string; size: number }> = ({ url, name, size }): React.ReactElement => (
    url
        ? <img src={url} alt={name} draggable={false} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flex: 'none', display: 'block', boxShadow: '0 0 0 6px var(--wd-card), 0 0 0 9px color-mix(in srgb, var(--wd-accent) 30%, transparent)' }} />
        : <span className="wd-num" aria-hidden="true" style={{ width: size, height: size, borderRadius: '50%', flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'var(--wd-frame)', color: '#fff', fontSize: size * 0.36 }}>{initials(name)}</span>
);

// #region RepCard
const RepCard: React.FunctionComponent<{ rep: DeckRep; wide: boolean }> = ({ rep, wide }): React.ReactElement => {
    const { mode, onAction } = useSlide();
    const lines = [rep.phone, rep.email].filter((x): x is string => !!x);
    return (
        <article className="wd-card wd-tint" style={{ display: 'flex', flexDirection: wide ? 'row' : 'column', alignItems: wide ? 'center' : 'flex-start', gap: wide ? 64 : 36, padding: wide ? '64px 72px' : '56px', minWidth: 0 }}>
            <Avatar url={rep.photo_url} name={rep.name} size={wide ? 260 : 200} />
            <div style={{ minWidth: 0 }}>
                <h3 className="wd-h" style={{ fontSize: fs(wide ? 60 : 48) }}>{rep.name}</h3>
                {rep.title ? <p className="wd-muted" style={{ fontSize: fs(28), marginTop: 10 }}>{rep.title}</p> : null}
                {lines.length ? <div style={{ marginTop: 32, display: 'grid', gap: 8, fontSize: fs(27), color: 'var(--wd-ink)' }}>{lines.map((l) => <span key={l}>{l}</span>)}</div> : null}
                {rep.booking_link ? (
                    mode === 'view'
                        ? <a className="wd-pill" href={rep.booking_link} target="_blank" rel="noopener noreferrer" onClick={() => onAction?.('cta', rep.booking_link ?? '')} style={{ marginTop: 32, textDecoration: 'none' }}>Book a time · {prettyUrl(rep.booking_link)}</a>
                        : <span className="wd-pill" style={{ marginTop: 32 }}>Book a time · {prettyUrl(rep.booking_link)}</span>
                ) : null}
            </div>
        </article>
    );
};

// #region TeamLayout
/** Who the lead works with: the rep's card (from their profile) and up to three more people. */
export const TeamLayout: React.FunctionComponent = (): React.ReactElement => {
    const { slide, computed, context, mode, edit } = useSlide();
    const rep = bool(slide.fields.show_rep, true) ? computed.team?.rep ?? null : null;
    const people = items(slide.fields.items);
    const n = people.length;
    return (
        <>
            <div className="wd-pad">
                <Head noEyebrow />
                <Fit style={{ flex: 1, display: 'flex', gap: 40, alignItems: 'stretch', flexDirection: rep && n ? 'row' : 'column', justifyContent: 'center' }}>
                    {rep ? <div style={{ flex: n ? '0 0 760px' : '1', display: 'flex', minWidth: 0 }}><RepCard rep={rep} wide={!n} /></div> : null}
                    {!rep && bool(slide.fields.show_rep, true) ? <EmptyNote style={{ flex: n ? 'none' : 1, minHeight: 200 }}>Your rep card (photo, title, phone, booking link) shows here from your profile.</EmptyNote> : null}
                    {n ? (
                        // Beside the rep card, people stack as compact rows; on their own they stand as cards in a row.
                        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: rep ? '1fr' : `repeat(${n}, minmax(0, 1fr))`, gap: rep ? 24 : 32, alignContent: 'center', minWidth: 0 }}>
                            {people.map((p, i) => {
                                const size = rep ? 120 : 200;
                                return (
                                    <article key={i} className="wd-card" style={rep
                                        ? { display: 'flex', alignItems: 'center', gap: 36, padding: '26px 36px', minWidth: 0 }
                                        : { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '56px 40px', minWidth: 0 }}>
                                        {edit || img(p.image)
                                            ? <ImageSlot fieldKey={`items.${i}.image`} image={img(p.image)} round label="Photo" style={{ width: size, height: size, flex: 'none' }} />
                                            : <Avatar url={null} name={fill(p.title ?? '', context.values, mode)} size={size} />}
                                        <div style={{ minWidth: 0, marginTop: rep ? 0 : 28 }}>
                                            <ItemText index={i} part="title" className="wd-h" style={{ fontSize: fs(rep ? 34 : 38), letterSpacing: 0 }} placeholder="Name" max={60} />
                                            <ItemText index={i} part="body" className="wd-muted" style={{ fontSize: fs(rep ? 24 : 25), marginTop: 8 }} placeholder="Role" max={80} />
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    ) : null}
                </Fit>
            </div>
            <Logo />
        </>
    );
};
